import { gql } from 'graphql-request';
import { graphqlClient, updateGraphQLClientAuth } from './graphqlClient';
import { supabase } from '@/integrations/supabase/client';

// Types based on the GraphQL schema
export interface Collection {
  id: string;
  name: string;
  description?: string;
  owner: string;
  created_at: string;
  updated_at: string;
  website_slug?: string;
  // Website PDF tracking
  pdf_last_exported_at?: string;
  pdf_export_settings?: {
    format: string;
    quality: string;
    filename: string;
    exported_at: string;
  };
  // Status management
  canva_design_status?: 'draft' | 'published';
  canva_design_thumbnail?: string;
  published_at?: string;
  last_modified_at?: string;
  assets?: Asset[];
}

export interface Asset {
  id: string;
  collection_id: string;
  type: 'simple' | 'diff';
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  
  // New organized image fields
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  secondary_version?: string;
  
  // Legacy fields - kept for backward compatibility during migration
  primary_image?: string;
  secondary_image?: string;
  
  crop_aspect_ratio?: string;
  crop_coordinates?: any; // LEGACY: JSONB data for crop coordinates (deprecated)
  
  // New explicit coordinate fields
  primary_crop_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  primary_markup_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  secondary_crop_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  secondary_markup_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };

  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  is_favorited?: boolean;
  header?: string;
  subheader?: string;
  logo_url?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
  primary_viewport_state?: any;
  secondary_viewport_state?: any;
  company_slug?: string;
  secondary_page_id?: string;
  primary_marker_x?: number | null;
  primary_marker_y?: number | null;
  secondary_marker_x?: number | null;
  secondary_marker_y?: number | null;
  created_at: string;
  updated_at: string;
  collection?: Collection;
  page?: {
    id: string;
    subslug: string;
    pricing_page_url?: string;
    company?: {
      id: string;
      slug: string;
      logo_url?: string;
    };
  };
}

// GraphQL Queries
const GET_COLLECTIONS_QUERY = gql`
  query GetCollections {
    collections {
      id
      name
      description
      owner
      created_at
      updated_at
    }
  }
`;

// Simple introspection query to test GraphQL connection
const INTROSPECTION_QUERY = gql`
  query IntrospectionQuery {
    __schema {
      queryType {
        name
      }
    }
  }
`;

// Test query to check if collections endpoint exists
const TEST_COLLECTIONS_QUERY = gql`
  query TestCollections {
    __type(name: "Collection") {
      name
      fields {
        name
        type {
          name
        }
      }
    }
  }
`;

const GET_COLLECTION_QUERY = gql`
  query GetCollection($id: ID!) {
    collection(id: $id) {
      id
      name
      description
      owner
      created_at
      updated_at
      assets {
        id
        collection_id
        type
        page_id
        slug
        version
        title
        description
        # New organized image fields
        primary_original_url
        primary_cropped_url
        primary_markup_url
        secondary_original_url
        secondary_cropped_url
        secondary_markup_url
        secondary_version
        # Legacy fields for backward compatibility
        primary_image
        secondary_image
        crop_aspect_ratio
        crop_coordinates
        # TODO: Enable after backend GraphQL schema is updated
        # primary_crop_coordinates
        # primary_markup_coordinates
        # secondary_crop_coordinates
        # secondary_markup_coordinates
        header
        subheader
        logo_url
        primary_viewport_state
        secondary_viewport_state
        company_slug
        secondary_page_id
        created_at
        updated_at
        page {
          id
          subslug
          pricing_page_url
          company {
            id
            slug
            logo_url
          }
        }
      }
    }
  }
`;

const GET_ASSETS_QUERY = gql`
  query GetAssets($collection_id: ID) {
    assets(collection_id: $collection_id) {
      id
      collection_id
      type
      page_id
      slug
      version
      title
      description
      # New organized image fields
      primary_original_url
      primary_cropped_url
      primary_markup_url
      secondary_original_url
      secondary_cropped_url
      secondary_markup_url
      secondary_version
      # Legacy fields for backward compatibility
      primary_image
      secondary_image
      crop_aspect_ratio
      crop_coordinates
      # New explicit coordinate fields
      primary_crop_coordinates
      primary_markup_coordinates
      secondary_crop_coordinates
      secondary_markup_coordinates
      comparison_mode
      is_favorited
      header
      subheader
      logo_url
      primary_viewport_state
      secondary_viewport_state
      created_at
      updated_at
      collection {
        id
        name
      }
      page {
        id
        subslug
        pricing_page_url
        company {
          id
          slug
          logo_url
        }
      }
    }
  }
`;

// GraphQL Mutations
const CREATE_COLLECTION_MUTATION = gql`
  mutation CreateCollection($input: CollectionInput!) {
    createCollection(input: $input) {
      id
      name
      description
      owner
      created_at
      updated_at
    }
  }
`;

const UPDATE_COLLECTION_MUTATION = gql`
  mutation UpdateCollection($id: ID!, $input: CollectionUpdateInput!) {
    updateCollection(id: $id, input: $input) {
      id
      name
      description
      owner
      created_at
      updated_at
    }
  }
`;

const DELETE_COLLECTION_MUTATION = gql`
  mutation DeleteCollection($id: ID!) {
    deleteCollection(id: $id) {
      id
      success
      message
    }
  }
`;

const CREATE_ASSET_MUTATION = gql`
  mutation CreateAsset($input: AssetInput!) {
    createAsset(input: $input) {
      id
      collection_id
      type
      page_id
      slug
      version
      title
      description
      primary_image
      secondary_image
      primary_original_url
      primary_cropped_url
      primary_markup_url
      secondary_original_url
      secondary_cropped_url
      secondary_markup_url
      secondary_version
      header
      subheader
      comparison_mode
      secondary_page_id
      created_at
      updated_at
    }
  }
`;

const UPDATE_ASSET_MUTATION = gql`
  mutation UpdateAsset($id: ID!, $input: AssetUpdateInput!) {
    updateAsset(id: $id, input: $input) {
      id
      collection_id
      type
      page_id
      slug
      version
      title
      description
      # New organized image fields
      primary_original_url
      primary_cropped_url
      primary_markup_url
      secondary_original_url
      secondary_cropped_url
      secondary_markup_url
      secondary_version
      # Legacy fields for backward compatibility
      primary_image
      secondary_image
      crop_aspect_ratio
      crop_coordinates
      # New explicit coordinate fields
      primary_crop_coordinates
      primary_markup_coordinates
      secondary_crop_coordinates
      secondary_markup_coordinates
      header
      subheader
      logo_url
      created_at
      updated_at
    }
  }
`;

const DELETE_ASSET_MUTATION = gql`
  mutation DeleteAsset($id: ID!) {
    deleteAsset(id: $id) {
      id
      success
      message
    }
  }
`;

// Test GraphQL connection first
export async function testGraphQLConnection(): Promise<boolean> {
  try {
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request(INTROSPECTION_QUERY);
    return true;
  } catch (error) {
    return false;
  }
}

// Test if Collections endpoint is available
export async function testCollectionsEndpoint(): Promise<boolean> {
  try {
    
    // First test basic GraphQL connection
    const connectionWorks = await testGraphQLConnection();
    if (!connectionWorks) {
      return false;
    }
    
    // Then test Collection type specifically
    const response = await graphqlClient.request(TEST_COLLECTIONS_QUERY);
    return !!response.__type;
  } catch (error) {
    return false;
  }
}

// Collection API functions
export async function fetchCollections(): Promise<Collection[]> {
  try {
    
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request<{ collections: Collection[] }>(GET_COLLECTIONS_QUERY);
    
    // Ensure we always return an array
    if (Array.isArray(response)) {
      return response;
    } else if (response && response.collections && Array.isArray(response.collections)) {
      return response.collections;
    } else {
      return [];
    }
  } catch (error) {
    
    // Check for common GraphQL errors that indicate the endpoint isn't implemented
    const errorMessage = error.message || '';
    if (
      errorMessage.includes('Cannot query field "collections"') ||
      errorMessage.includes('Field "collections" doesn\'t exist') ||
      errorMessage.includes('Unknown field "collections"') ||
      error.response?.status === 400
    ) {
      return [];
    }
    
    // For other errors, re-throw to show error state
    throw error;
  }
}

export async function fetchCollection(id: string): Promise<Collection> {
  try {
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request<{ collection: Collection }>(GET_COLLECTION_QUERY, { id });
    return response.collection;
  } catch (error) {
    throw error;
  }
}

export async function fetchAssets(collection_id?: string): Promise<Asset[]> {
  try {
    // Query directly from app_assets with joins to get company info
    // This ensures we get all columns including marker coordinates
    let query = supabase
      .from('app_assets')
      .select(`
        *,
        page:pages!inner(
          id,
          subslug,
          pricing_page_url,
          company:companies!inner(
            id,
            slug,
            logo_url
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (collection_id) {
      query = query.eq('collection_id', collection_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }

    // Transform the data to match the Asset interface structure
    const transformedData = (data || []).map((asset: any) => ({
      ...asset,
      logo_url: asset.page?.company?.logo_url,
      company_slug: asset.page?.company?.slug,
    }));

    return transformedData;
  } catch (error: any) {
    console.error('Error in fetchAssets:', error);
    throw error;
  }
}

export async function createCollection(input: { name: string; description?: string }): Promise<Collection> {
  try {
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request<{ createCollection: Collection }>(CREATE_COLLECTION_MUTATION, { input });
    return response.createCollection;
  } catch (error) {
    
    // Check if it's because the mutation doesn't exist
    const errorMessage = error.message || '';
    if (
      errorMessage.includes('Cannot query field "createCollection"') ||
      errorMessage.includes('Unknown field "createCollection"') ||
      error.response?.status === 400
    ) {
      throw new Error('Collections API is not implemented yet. Please contact the development team to deploy the Collections backend.');
    }
    
    throw error;
  }
}

export async function updateCollection(id: string, input: { name?: string; description?: string }): Promise<Collection> {
  try {
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request<{ updateCollection: Collection }>(UPDATE_COLLECTION_MUTATION, { id, input });
    return response.updateCollection;
  } catch (error) {
    throw error;
  }
}

export async function deleteCollection(id: string): Promise<{ id: string; success: boolean; message: string }> {
  try {
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request<{ deleteCollection: { id: string; success: boolean; message: string } }>(DELETE_COLLECTION_MUTATION, { id });
    return response.deleteCollection;
  } catch (error) {
    throw error;
  }
}

export async function createAsset(input: {
  collection_id: string;
  type: 'simple' | 'diff';
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  primary_image?: string;
  secondary_image?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  header?: string;
  subheader?: string;
  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  secondary_version?: string;
  secondary_page_id?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
}): Promise<Asset> {
  try {
    
    // Note: The server should handle:
    // 1. If primary_image is a Cloudinary URL, use it as-is
    // 2. If primary_image is an S3 URL and no Cloudinary URL exists, upload to Cloudinary
    // 3. Store the final Cloudinary URL in primary_image
    // 4. Keep the S3 URL in secondary_image as backup
    
    await updateGraphQLClientAuth();
    const response = await graphqlClient.request<{ createAsset: Asset }>(CREATE_ASSET_MUTATION, { input });
    return response.createAsset;
  } catch (error) {
    throw error;
  }
}

export async function updateAsset(id: string, input: {
  type?: 'simple' | 'diff';
  page_id?: string;
  slug?: string;
  version?: string;
  title?: string;
  description?: string;
  primary_image?: string;
  secondary_image?: string;
  // New organized image fields
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  secondary_version?: string;
  crop_aspect_ratio?: string;
  crop_coordinates?: string | any; // LEGACY: Can be string or object, we'll handle conversion
  // New explicit coordinate fields
  primary_crop_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  primary_markup_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  secondary_crop_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  secondary_markup_coordinates?: {
    x: number;
    y: number;
    scale: number;
    viewportWidth: number;
    viewportHeight: number;
  };
  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  header?: string;
  subheader?: string;
}): Promise<Asset> {
  try {
    // Use direct Supabase query instead of GraphQL (project is migrating away from GraphQL)
    const { data, error } = await supabase
      .from('app_assets')
      .update(input)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Asset not found');

    return data as Asset;
  } catch (error) {
    console.error('Error updating asset:', error);
    throw error;
  }
}

export async function deleteAsset(id: string): Promise<{ id: string; success: boolean; message: string }> {
  try {
    // Use direct Supabase query instead of GraphQL (project is migrating away from GraphQL)
    const { error } = await supabase
      .from('app_assets')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { id, success: true, message: 'Asset deleted successfully' };
  } catch (error) {
    console.error('Error deleting asset:', error);
    throw error;
  }
}

// Debug function to test Collections endpoint - exposed globally for console testing
declare global {
  interface Window {
    testCollectionsAPI?: () => Promise<void>;
  }
}

if (typeof window !== 'undefined') {
  window.testCollectionsAPI = async () => {
  };
  
}